// The office's job sheet (office-booking and office-review prototypes): a job's estimates, built,
// sent and OK'd in the office, and cancelling the job. Shapes are those of jobs.get,
// estimates.listForJob / estimates.get and pricebook.listItems; who sees which control is the
// permission its action asks for (packages/auth permissions.ts), never a role name (QA-681). Beside
// each, the system roles that hold it.

import { newId } from '../shared/ids.ts';
import { minutesText } from '../books/timesheet-model.ts';
import { dateTimeText, sentences } from '@flowology/kernel';
import { MAX_ESTIMATE_OPTIONS, memberPrice, type MemberPlan } from '@flowology/domain';
import {
  invoiceState,
  money,
  type ImportedEvent,
  type InvoiceSummary,
  type JobDetail,
  type TimeLogged,
} from '../review/review-model.ts';

/** estimates.build (estimates.save): the owner and the manager; in the field, the technician. */
export const ESTIMATE_BUILD_PERMS = ['estimates.build'];
/** estimates.approve (estimates.approvePrice): the owner, the manager and the dispatcher. */
export const PRICE_APPROVE_PERMS = ['estimates.approve'];
/** messages.send (estimates.send): the office's four who talk to customers. */
export const SEND_PERMS = ['messages.send'];
/** jobs.cancel: the owner, the manager and the dispatcher. */
export const CANCEL_PERMS = ['jobs.cancel'];
/** jobs.dispatch (appointments.add, a return visit): the owner, the manager and the dispatcher. */
export const DISPATCH_PERMS = ['jobs.dispatch'];
/** invoices.po.set (invoices.setPo, the customer's PO on a job, F-29; QA-514): the office five, CSR included. */
export const PO_PERMS = ['invoices.po.set'];
/** jobs.book (estimates.bookSold): the office's four who book. */
export const BOOK_PERMS = ['jobs.book'];
/** purchases.record (jobPurchases.forJob, Parts bought; QA-602): not the CSR; in the field, the technician. */
export const PURCHASE_PERMS = ['purchases.record'];
/** Who reads invoices and their deposits (invoices.view, BL-006's deposits.list): everyone but the warehouse. */
export const INVOICE_VIEW_PERMS = ['invoices.view'];

/** Whether the person holds any of these permissions (shared/may.ts). */
export { holdsAny as holds } from '../shared/may.ts';

export interface EstimateLine {
  id: string;
  pricebookItemId: string | null;
  type: string;
  description: string;
  quantity: string;
  unitPriceCents: number;
  bookPriceCents: number | null;
  overrideReason: string | null;
  /** 'needed' while it waits for the office's OK, 'approved' once given. */
  overrideApproval: string | null;
  totalCents: number;
  /** One from ServiceTitan: its item's code, as ServiceTitan shows it (history fix 2). */
  code?: string | null;
  /** Seen by the customer, not signed: today's book price, when the book moved since it was shown. */
  bookNowCents?: number;
}

export interface EstimateOption {
  id: string;
  title: string;
  summary: string | null;
  tier: string | null;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  lines: EstimateLine[];
  /** BL-059: the verdict and percents only for roles that see our cost; `waits` for everyone. */
  margin?: {
    verdict?: 'off' | 'not_checked' | 'ok' | 'warn' | 'below_min';
    pct?: number | null;
    minPct?: number;
    warnPct?: number;
    /** Lines with no cost on record, left out of the margin. */
    uncosted?: number;
    waits: boolean;
    approved: boolean;
  };
}

export interface Estimate {
  id: string;
  number: string;
  jobId: string;
  title: string;
  status: string;
  disposition: string | null;
  invoiceEffect: string | null;
  parentEstimateId: string | null;
  selectedOptionId: string | null;
  soldAt: string | null;
  frozen: boolean;
  options: EstimateOption[];
  signature: { signerName: string; signedAt: string; method: string } | null;
  /** BL-059: the customer signed this option while it waited for the office's OK. */
  waitingSale?: { id: string; optionId: string; signerName: string; signedAt: string } | null;
  /** It came over from ServiceTitan: ServiceTitan's while the company mirrors it. */
  imported?: boolean;
  /** ServiceTitan took it off after the customer saw it (history fix 2). */
  removedInServiceTitan?: boolean;
  /** Who sold it: the person here, else who ServiceTitan says did (history fix 2). */
  soldBy?: string | null;
  /** How its latest send went (QA-871): Sent only once a text or email of it has gone. */
  delivery?: { state: 'sending' | 'sent' | 'failed'; reason: string | null } | null;
}

export interface PricebookItem {
  id: string;
  type: string;
  code: string;
  name: string;
  unit: string;
  priceCents: number;
  /** Its own member price (pricebook.listItems), which beats a plan's percentage (M8). */
  memberPriceCents?: number | null;
  /** Only for roles that may see our cost. */
  costCents?: number | null;
}

// ── what the job sheet says about the job (history fix 1) ──

/**
 * The job's business unit, priority and project, as ServiceTitan's job page heads them: "Service ·
 * Priority low · Project #1042". What isn't known is left out; null with nothing.
 */
export function jobFactsText(job: Pick<JobDetail, 'businessUnitName' | 'fromServiceTitan'>): string | null {
  const st = job.fromServiceTitan;
  const parts = [
    job.businessUnitName,
    st?.priority ? `Priority ${st.priority.toLowerCase()}` : null,
    st?.projectId ? `Project #${st.projectId}` : null,
  ].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/** A visit's technicians, the lead first: "Ann Lee, Raj Patel"; "No technician" with none. */
export function visitTechsText(techs: readonly { name: string }[] | undefined): string {
  return techs?.length ? techs.map((t) => t.name).join(', ') : 'No technician';
}

/** One person's time on the job: "13 h 46 min working, 25 min driving", and "still on the clock". */
export function timeLoggedText(t: TimeLogged): string {
  const parts = [
    t.workMinutes ? `${minutesText(t.workMinutes)} working` : null,
    t.driveMinutes ? `${minutesText(t.driveMinutes)} driving` : null,
  ].filter((x): x is string => !!x);
  const said = parts.length ? parts.join(', ') : 'Under a minute';
  return t.running ? `${said}, still on the clock` : said;
}

/**
 * The job's invoice, its total with tax and what's still owed: "Invoice 1042 · $8,201.54 incl. tax ·
 * Paid · balance $0.00".
 */
export function invoiceLine(
  inv: Pick<InvoiceSummary, 'number' | 'status' | 'totalCents' | 'taxCents' | 'balanceCents'>,
): string {
  const total = inv.taxCents ? `${money(inv.totalCents)} incl. tax` : money(inv.totalCents);
  return `Invoice ${inv.number} · ${total} · ${invoiceState(inv)} · balance ${money(inv.balanceCents)}`;
}

/** An option's amount before tax, said beneath its total with tax: "$386.45 before tax"; null untaxed. */
export function beforeTaxText(o: { subtotalCents: number; taxCents: number }): string | null {
  return o.taxCents ? `${money(o.subtotalCents)} before tax` : null;
}

// ── what came over from ServiceTitan with the job (history fix 2) ──

/**
 * The job's "In ServiceTitan" section: its hold and cancel reasons on their own ("On hold: Waiting on
 * parts"), undated (ServiceTitan gives them none), then each event oldest first, as the event, what it
 * says, and when ("Job Booked", "Booked by phone", "Oct 18, 2026, 9:05 a.m.").
 */
export function importedHistoryRows(
  events: readonly ImportedEvent[],
  timeZone: string,
): { reasons: string[]; events: { event: string; detail: string | null; when: string }[] } {
  const reasons = events
    .filter((e) => e.kind !== 'history')
    .map((e) => `${e.kind === 'hold_reason' ? 'On hold' : 'Cancelled'}: ${e.text}`);
  const rows = events
    .filter((e) => e.kind === 'history')
    .sort((a, b) => a.at.localeCompare(b.at))
    .map((e) => {
      // the load keeps ServiceTitan's event and its words as "Event: words" (load.ts)
      const cut = e.text.indexOf(': ');
      return {
        event: cut > 0 ? e.text.slice(0, cut) : e.text,
        detail: cut > 0 ? e.text.slice(cut + 2) || null : null,
        when: dateTimeText(e.at, timeZone),
      };
    });
  return { reasons, events: rows };
}

/**
 * What a line from ServiceTitan says beside its description (history fix 2): its item's code and the
 * technician it's credited to, "PLB-101 · Sarah Lee"; null on a line with neither (Flowology's own).
 */
export function lineTagsText(l: { code?: string | null; technician?: string | null }): string | null {
  const parts = [l.code, l.technician].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/** Who sold it, said once: "Sold by Ann Lee"; null when no one is known. */
export const soldByText = (soldBy: string | null | undefined) => (soldBy ? `Sold by ${soldBy}` : null);

/** Who filled in a form and when: "Filled in by Ann Lee · Oct 20, 2026, 9:15 a.m."; "Filled in" with no one known. */
export function filledInText(
  sub: { submittedBy?: string | null; submittedAt: string },
  timeZone: string,
): string {
  return `Filled in${sub.submittedBy ? ` by ${sub.submittedBy}` : ''} · ${dateTimeText(sub.submittedAt, timeZone)}`;
}

/** An estimate's state in the office's words. */
export function estimateState(e: Estimate): string {
  if (e.status === 'sold') return e.disposition === 'sold_later' ? 'Sold, for later' : 'Sold, on this visit';
  // Sent only once a text or email of it has gone (QA-871)
  if (e.status === 'sent' && e.delivery?.state === 'sending') return 'Sending to the customer';
  if (e.status === 'sent' && e.delivery?.state === 'failed')
    return `Not sent: ${e.delivery.reason ?? 'the message failed'}`;
  return (
    {
      draft: 'Draft',
      presented: 'Shown to the customer',
      sent: 'Sent to the customer',
      declined: 'Declined',
      expired: 'Expired',
    }[e.status] ?? e.status
  );
}

/** The option's lines that wait for the office to OK their price (Q44). */
export const waitingLines = (o: EstimateOption) => o.lines.filter((l) => l.overrideApproval === 'needed');

/**
 * An option's margin in words (BL-059), for those who see our cost: "Margin 37.5 %: under the 40 %
 * minimum", "Margin 68.7 %: under the 70 % we aim for", "Margin 72.1 %", with "on the lines with a
 * cost; 1 line has no cost" when some are left out, or "Margin not checked: no line has a cost".
 * Null when margin control is off, or the server sent no verdict (no cost access).
 */
export function marginText(o: EstimateOption): { text: string; under: boolean } | null {
  const m = o.margin;
  if (!m?.verdict || m.verdict === 'off') return null;
  if (m.verdict === 'not_checked') return { text: 'Margin not checked: no line has a cost', under: false };
  const n = m.uncosted ?? 0;
  const some = n ? ' on the lines with a cost' : '';
  const left = n ? `; ${n} ${n === 1 ? 'line has' : 'lines have'} no cost` : '';
  const pct = `Margin ${m.pct} %${some}`;
  if (m.verdict === 'below_min')
    return { text: `${pct}: under the ${m.minPct} % minimum${left}`, under: true };
  if (m.verdict === 'warn')
    return { text: `${pct}: under the ${m.warnPct} % we aim for${left}`, under: true };
  return { text: `${pct}${left}`, under: false };
}

/** estimates.send refuses these; the button isn't offered for them. */
export const sendable = (e: Estimate) =>
  !['sold', 'declined', 'expired'].includes(e.status) && e.options.length > 0;

/** An estimate sold for later waits to be booked from To book (estimates.bookSold). */
export const bookable = (e: Estimate) => e.status === 'sold' && e.disposition === 'sold_later';

// ── the editor ──

export interface DraftLine {
  lineId: string;
  pricebookItemId: string | null;
  description: string;
  quantity: string;
  /** The price typed, in dollars ("189.00"); the book price until changed. */
  price: string;
  bookPriceCents: number | null;
  /** Priced for a member (M8): the plan, and the regular price the member price is off. */
  member?: { planName: string; listPriceCents: number } | null;
  /** What it costs us, for a line not from the price book (optional). */
  cost: string;
  reason: string;
  /**
   * A seen estimate's line whose book moved since (estimates.get's bookNowCents): today's price, which
   * it takes only when asked ("Use today's price"), and the quantity it was shown in.
   */
  bookNowCents?: number | null;
  shownQuantity?: string;
  /** Asked for today's book price instead of the one it was shown at (estimates.save useTodaysPrice). */
  useTodaysPrice?: boolean;
}

export interface DraftOption {
  optionId: string;
  title: string;
  tier: '' | 'good' | 'better' | 'best';
  summary: string;
  lines: DraftLine[];
}

export interface Draft {
  estimateId: string;
  jobId: string;
  title: string;
  options: DraftOption[];
}

export const dollars = (cents: number) => (cents / 100).toFixed(2);

/** The server's quantity as typed: "1.000" → "1", "1.500" → "1.5". */
export const qtyText = (q: string) => (q.includes('.') ? q.replace(/\.?0+$/, '') : q);

/** "189", "189.5", "$1,189.50" → cents; null when it isn't a price. */
export function centsOf(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

const QUANTITY = /^\d{1,9}(\.\d{1,3})?$/;

const TIERS: DraftOption['tier'][] = ['good', 'better', 'best'];

export function blankOption(i: number): DraftOption {
  const tier = TIERS[i] ?? '';
  return {
    optionId: newId(),
    title: tier ? tier[0]!.toUpperCase() + tier.slice(1) : `Option ${i + 1}`,
    tier,
    summary: '',
    lines: [],
  };
}

/** Another option fits: an estimate offers at most six (packages/domain), as the server holds it to. */
export const canAddOption = (d: Pick<Draft, 'options'>): boolean => d.options.length < MAX_ESTIMATE_OPTIONS;

export function newDraft(jobId: string, title: string): Draft {
  return { estimateId: newId(), jobId, title, options: [blankOption(0)] };
}

/** An unsigned estimate as the editor holds it: its own ids, so saving again replaces it. */
export function draftOf(e: Estimate): Draft {
  return {
    estimateId: e.id,
    jobId: e.jobId,
    title: e.title,
    options: e.options.map((o) => ({
      optionId: o.id,
      title: o.title,
      tier: (TIERS as string[]).includes(o.tier ?? '') ? (o.tier as DraftOption['tier']) : '',
      summary: o.summary ?? '',
      lines: o.lines.map((l) => ({
        lineId: l.id,
        pricebookItemId: l.pricebookItemId,
        description: l.description,
        quantity: qtyText(l.quantity),
        price: dollars(l.unitPriceCents),
        bookPriceCents: l.bookPriceCents,
        cost: '',
        reason: l.overrideReason ?? '',
        ...(l.bookNowCents !== undefined
          ? { bookNowCents: l.bookNowCents, shownQuantity: qtyText(l.quantity) }
          : {}),
      })),
    })),
  };
}

/**
 * What a line from the book costs this customer, as estimates.save prices it: at a member's address,
 * the member price, by the one rule (@flowology/domain memberPrice); the book price otherwise.
 */
export const bookPriceFor = (item: PricebookItem, plans: readonly MemberPlan[]) =>
  memberPrice(
    {
      id: item.id,
      type: item.type,
      priceCents: item.priceCents,
      memberPriceCents: item.memberPriceCents ?? null,
    },
    plans,
  );

/** A line from the book, at the price the save will give it (a member's at a member's address). */
export function itemLine(item: PricebookItem, plans: readonly MemberPlan[] = []): DraftLine {
  const p = bookPriceFor(item, plans);
  return {
    lineId: newId(),
    pricebookItemId: item.id,
    description: item.name,
    quantity: '1',
    price: dollars(p.priceCents),
    bookPriceCents: p.priceCents,
    ...(p.memberPriceOf && p.planName && p.listPriceCents !== null
      ? { member: { planName: p.planName, listPriceCents: p.listPriceCents } }
      : {}),
    cost: '',
    reason: '',
  };
}

export function freeLine(): DraftLine {
  return {
    lineId: newId(),
    pricebookItemId: null,
    description: '',
    quantity: '1',
    price: '',
    bookPriceCents: null,
    cost: '',
    reason: '',
  };
}

/**
 * "Use today's price": the line at today's book price, as estimates.save will price it, instead of the
 * price the customer was shown. Explicit only: a re-save otherwise keeps the shown price.
 */
export function takeTodaysPrice(l: DraftLine): DraftLine {
  if (l.bookNowCents == null) return l;
  return {
    ...l,
    price: dollars(l.bookNowCents),
    bookPriceCents: l.bookNowCents,
    reason: '',
    bookNowCents: null,
    useTodaysPrice: true,
  };
}

/**
 * A new quantity. A seen line keeps its shown price only in the quantity it was shown in: in another,
 * estimates.save prices it at today's book, so a line still at its book price shows today's price now.
 */
export function withQuantity(l: DraftLine, quantity: string): DraftLine {
  const next = { ...l, quantity };
  const moved =
    l.bookNowCents != null &&
    l.shownQuantity !== undefined &&
    QUANTITY.test(quantity.trim()) &&
    Number(quantity) !== Number(l.shownQuantity);
  return moved && !needsReason(l) ? takeTodaysPrice(next) : next;
}

/** A price different from the book (or any line not from it) needs a reason, as the server says. */
export const needsReason = (l: DraftLine) =>
  l.bookPriceCents === null || centsOf(l.price) !== l.bookPriceCents;

/** The line's total as the server will work it out (quantity × price, rounded half up), or null. */
export function lineCents(l: DraftLine): number | null {
  const p = centsOf(l.price);
  if (p === null || !QUANTITY.test(l.quantity.trim())) return null;
  return Math.round(Number(l.quantity) * p);
}

export const optionCents = (o: DraftOption) => o.lines.reduce((s, l) => s + (lineCents(l) ?? 0), 0);

/** What still stops the draft from saving, in words; empty when it can go. */
export function draftProblems(d: Draft): string[] {
  const out: string[] = [];
  if (!d.title.trim()) out.push('Give the estimate a title.');
  d.options.forEach((o, oi) => {
    const name = o.title.trim() || `Option ${oi + 1}`;
    if (!o.title.trim()) out.push(`Option ${oi + 1} needs a title.`);
    if (!o.lines.length) out.push(`${name} needs at least one line.`);
    o.lines.forEach((l, li) => {
      const at = `${name}, line ${li + 1}`;
      if (!l.description.trim()) out.push(`${at}: say what it is.`);
      if (!QUANTITY.test(l.quantity.trim())) out.push(`${at}: a quantity like 1 or 1.5.`);
      if (centsOf(l.price) === null) out.push(`${at}: a price like 189.00.`);
      else if (needsReason(l) && !l.reason.trim())
        out.push(
          l.bookPriceCents === null
            ? `${at}: say why (a line not from the price book needs a reason).`
            : `${at}: say why the price changed.`,
        );
      if (l.pricebookItemId === null && l.cost.trim() && centsOf(l.cost) === null)
        out.push(`${at}: a cost like 40.00, or leave it blank.`);
    });
  });
  return out;
}

/** estimates.save's input. Only valid drafts (draftProblems empty) are sent. */
export function saveInput(d: Draft) {
  return {
    estimateId: d.estimateId,
    jobId: d.jobId,
    title: d.title.trim(),
    options: d.options.map((o) => ({
      optionId: o.optionId,
      title: o.title.trim(),
      ...(o.summary.trim() ? { summary: o.summary.trim() } : {}),
      ...(o.tier ? { tier: o.tier } : {}),
      lines: o.lines.map((l) => {
        const price = centsOf(l.price)!;
        const free = l.pricebookItemId === null;
        const cost = free && l.cost.trim() ? centsOf(l.cost) : null;
        return {
          lineId: l.lineId,
          ...(free ? {} : { pricebookItemId: l.pricebookItemId! }),
          description: l.description.trim(),
          quantity: l.quantity.trim(),
          ...(free || price !== l.bookPriceCents ? { unitPriceCents: price } : {}),
          ...(needsReason(l) ? { overrideReason: l.reason.trim() } : {}),
          ...(cost !== null ? { unitCostCents: cost } : {}),
          ...(l.useTodaysPrice ? { useTodaysPrice: true as const } : {}),
        };
      }),
    })),
  };
}

// ── cancelling ──

/** The reasons the office-booking prototype offers; "Something else" asks for the words. */
export const CANCEL_REASONS = [
  'Customer asked to cancel',
  'Booked twice',
  'Fixed before we arrived',
  'Couldn’t reach the customer',
  // F-08: the trip charge's own case (appointments.tripChargeCents: "a no-show or a late cancel")
  'Customer wasn’t home (no-show)',
  'Weather or access',
];

export function cancelReason(choice: string, note: string): string {
  const n = note.trim();
  return choice ? (n ? `${choice}: ${n}` : choice) : n;
}

export const cancellable = (status: string) => status !== 'completed' && status !== 'cancelled';

/** A visit still to come or under way: the one a cancel's text is about (QA-688). */
const OPEN_VISIT = ['scheduled', 'dispatched', 'working', 'paused'];

/** The job's next open visit, the earliest; null when none is (the text then names the job). */
export function visitToCancel<V extends { status: string; start: string }>(
  appointments: readonly V[],
): V | null {
  return (
    [...appointments]
      .filter((a) => OPEN_VISIT.includes(a.status))
      .sort((a, b) => a.start.localeCompare(b.start))[0] ?? null
  );
}

/**
 * The cancel's text to the customer (QA-688), the office's to change before it goes: "Hi Pat, it’s
 * Acme: your visit for Tue, Oct 27, 10 p.m.–12 a.m. is cancelled. Call us when you’d like another time."
 * `when` is the visit's day and window, or null for a job with none booked.
 */
export function cancelText(p: { firstName: string; company?: string | null; when: string | null }): string {
  const hi = p.company ? `Hi ${p.firstName}, it’s ${p.company}:` : `Hi ${p.firstName},`;
  return sentences(
    `${hi} ${p.when ? `your visit for ${p.when} is cancelled` : 'your booking with us is cancelled'}`,
    'Call us when you’d like another time',
  );
}

/** "INV-18353 was voided." and what's left on an invoice already issued, after a cancel (QA-689). */
export function cancelInvoiceWords(r: {
  voidedInvoices?: readonly { number: string }[];
  issuedInvoices?: readonly { number: string; balanceCents: number }[];
}): string {
  const voided = (r.voidedInvoices ?? []).map((i) => i.number);
  const issued = (r.issuedInvoices ?? []).map((i) =>
    i.balanceCents > 0
      ? `${i.number} was already issued, with ${money(i.balanceCents)} owing: void it or collect it from the invoice`
      : `${i.number} was already issued and is kept`,
  );
  return sentences(
    voided.length
      ? `${voided.join(' and ')} ${voided.length === 1 ? 'was' : 'were'} voided: nothing is owing on the job`
      : null,
    ...issued,
  );
}

// ── one visit (F-08) ──

export const VISIT_STATUS: Record<string, string> = {
  scheduled: 'Scheduled',
  dispatched: 'Dispatched',
  working: 'On site',
  paused: 'Paused',
  closed: 'Done',
  cancelled: 'Cancelled',
};

/**
 * A visit's state on the job sheet: "Done", and a cancelled one with why, as it was given ("Cancelled:
 * Removed in ServiceTitan", history fix 2).
 */
export function visitStateText(a: { status: string; cancelReason?: string | null }): string {
  const word = VISIT_STATUS[a.status] ?? a.status;
  return a.status === 'cancelled' && a.cancelReason ? `${word}: ${a.cancelReason}` : word;
}

/**
 * Whether one visit can be cancelled and the job kept (appointments.cancel's own rule): not started
 * (scheduled or dispatched), on a job still open, and not the job's only visit left; for that one the
 * job is cancelled instead.
 */
export function visitCancellable(
  job: { status: string; appointments: readonly { id: string; status: string }[] },
  visitId: string,
): boolean {
  const v = job.appointments.find((a) => a.id === visitId);
  if (!v || !cancellable(job.status) || (v.status !== 'scheduled' && v.status !== 'dispatched')) return false;
  return job.appointments.some((a) => a.id !== visitId && a.status !== 'cancelled');
}

// ── its notes (QA-690) ──

/**
 * Who wrote a note on the job and when, in the company's time: "Sam Rivera · Oct 25, 10:18 a.m.",
 * and "on a visit" for one written on a visit (the iPad's Visit screen). Newest first, as a thread reads.
 */
export function noteRows(
  notes: readonly { id: string; body: string; at: string; author: string; appointmentId: string | null }[],
  timeZone: string,
  now: Date = new Date(),
): { id: string; body: string; byline: string }[] {
  return [...notes]
    .sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id))
    .map((n) => ({
      id: n.id,
      body: n.body,
      byline: `${n.author} · ${dateTimeText(n.at, timeZone, { year: false, now })}${n.appointmentId ? ' · on a visit' : ''}`,
    }));
}
